import useBreakpoints from '@zenbusiness/zui/useBreakpoints';

import pageComponents from '#client/constants/pageComponents';
import useFlowConfig from '#client/hooks/flow/useFlowConfig';

function buildSizedComponentName(componentName, sizes) {
  let size = 'desktop';
  if (sizes.isMobile) {
    size = 'mobile';
  } else if (sizes.isTablet) {
    size = 'tablet';
  }
  return `${componentName}-${size}`;
}

/**
 * Custom hook to get the page configuration for the current flow page.
 * Handles responsive component names and merges flow-specific overrides.
 *
 * @param {string} flowPage - The current page path
 * @returns {import('#client/config/flowPageConfig').Page} The complete page configuration
 * @throws {Error} If no configuration is found for the given page
 */
function useFlowPageConfig(flowPage) {
  const { pages, pageConfigs } = useFlowConfig();
  const { sizes } = useBreakpoints();

  const currentPageConfig = pages.find((page) => page.path === flowPage);

  if (!currentPageConfig) {
    throw new Error(`No configuration found for page: ${flowPage}`);
  }

  const { pageComponent: baseComponentName, ...pageConfig } = currentPageConfig;

  // Try to find a responsive version of the component first
  const sizedComponentName = buildSizedComponentName(baseComponentName, sizes);
  const pageComponent = pageComponents[sizedComponentName] ? sizedComponentName : baseComponentName;

  // Look for overrides for either the responsive or base component name
  const pageConfigOverrides = pageConfigs[pageComponent] || pageConfigs[baseComponentName] || {};

  // Merge configs with overrides taking precedence
  return {
    ...pageConfig,
    ...pageConfigOverrides,
    pageComponent
  };
}

export default useFlowPageConfig;
